import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

import { useTranslation } from 'contexts/translation-context';

/**
 * Shared effect that surfaces silent Formik validation failures.
 *
 * Formik never calls `onSubmit` when the form is invalid, so a user pressing
 * "submit" on a form with an error they can't see (e.g. an off-screen field)
 * gets no feedback at all. This shows a toast whenever a submit attempt is
 * rejected by validation.
 *
 * Most forms get this for free through the project-local `Formik` / `useFormik`
 * wrappers in `components/form/formik`; you rarely need to call this directly.
 */
export function useFormErrorToast(submitCount: number, isValid: boolean, isValidating: boolean): void {
  const { i18n } = useTranslation();
  const handledSubmitCount = useRef(0);

  useEffect(() => {
    // Wait until validation for this submit attempt has settled.
    if (isValidating || submitCount === handledSubmitCount.current) {
      return;
    }
    handledSubmitCount.current = submitCount;
    if (!isValid) {
      toast.error(i18n('errors.formValidation'));
    }
  }, [submitCount, isValid, isValidating, i18n]);
}

/** Drop-in `<Formik>` child that triggers {@link useFormErrorToast}. */
export const FormErrorToast = () => {
  const { submitCount, isValid, isValidating } = useFormikContext();
  useFormErrorToast(submitCount, isValid, isValidating);
  return null;
};
